import { useState } from 'react'
import { useGameStore } from '@/store/gameStore'
import { ROSTER_RULES, getRosterCap, getEffectiveRating } from '@/config/playersConfig'
import { getPlayerStage, STAGE_DEFINITIONS } from '@/config/playerStageConfig'
import { PlayerAvatar } from '../components/PlayerAvatar'
import { PlayerDetailModal } from '../components/PlayerDetailModal'
import { playerPortraitSrc } from '../helpers/portraits'
import { MechanicHint } from '../components/MechanicHint'
import { SectionDeck, StatStrip, EmptyStateRich } from '../components/CourtVision'
import { useConfirm } from '../components/useConfirm'
import { TrendingUp, TrendingDown, Minus, UserMinus, Sprout, Flame, Medal, Users, HeartPulse } from 'lucide-react'

const STAGE_ICONS = { rising: Sprout, peak: Flame, veteran: Medal } as const

export function StageBadge({ age }: { age: number }) {
  const stage = getPlayerStage(age)
  const def = STAGE_DEFINITIONS[stage]
  const Icon = STAGE_ICONS[stage]
  return (
    <span className={`stage-badge ${def.className}`}>
      <Icon size={10} /> {def.label}
    </span>
  )
}

function FormTrend({ form }: { form: number }) {
  if (form >= 60) return <TrendingUp size={14} style={{ color: 'var(--padel-green)' }} />
  if (form <= 40) return <TrendingDown size={14} style={{ color: 'var(--danger)' }} />
  return <Minus size={14} style={{ color: 'var(--text-secondary)' }} />
}

function levelColor(level: number): string {
  if (level >= 70) return 'var(--victory-orange)'
  if (level >= 50) return 'var(--gold)'
  return 'var(--text-secondary)'
}

export function RosterTabContent() {
  const { players, releasePlayer, clubLevel } = useGameStore()
  const { confirm, confirmDialog } = useConfirm()
  const [detailPlayerId, setDetailPlayerId] = useState<string | null>(null)
  const detailPlayer = players.find(p => p.id === detailPlayerId) ?? null

  const handleRelease = async (playerId: string, name: string, salary: number) => {
    const severance = salary * ROSTER_RULES.severanceWeeks
    if (await confirm({
      title: 'Rescindir contrato',
      icon: <UserMinus size={20} />,
      message: <>Vas a rescindir el contrato de <b>{name}</b>. Finiquito: <b>{severance.toLocaleString('es-ES')}€</b>.</>,
      confirmLabel: 'Rescindir',
      confirmTone: 'danger',
    })) {
      releasePlayer(playerId)
    }
  }

  const totalSalary = players.reduce((sum, p) => sum + p.salary, 0)

  if (players.length === 0) {
    return (
      <EmptyStateRich
        icon={Users}
        title="El banquillo está vacío"
        sub="Ficha jugadores en el Mercado para competir en torneos y ligas"
      />
    )
  }

  return (
    <div>
      {confirmDialog}
      <PlayerDetailModal
        player={detailPlayer}
        mode="roster"
        onClose={() => setDetailPlayerId(null)}
        onAction={() => {
          if (detailPlayer) {
            setDetailPlayerId(null)
            handleRelease(detailPlayer.id, detailPlayer.name, detailPlayer.salary)
          }
        }}
      />
      <SectionDeck title="Plantilla" icon={Users} meta={`${players.length}/${getRosterCap(clubLevel)} jugadores`}>
        <div style={{ marginBottom: '0.6rem' }}>
          <StatStrip
            label="Salarios de jugadores"
            value={<span className="money-negative">-{totalSalary.toLocaleString('es-ES')}€/sem</span>}
          />
        </div>

        <div className="space-y-2">
          {players.map(p => {
            const isRising = getPlayerStage(p.age) === 'rising'
            return (
              <div
                key={p.id}
                className="row-tile"
                style={{ flexDirection: 'column', alignItems: 'stretch', gap: '0.45rem', cursor: 'pointer' }}
                onClick={() => setDetailPlayerId(p.id)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <PlayerAvatar id={p.id} name={p.name} size={36} src={playerPortraitSrc(p)} />
                  <div className="row-tile-main">
                    <div className="row-tile-title">
                      {p.name}
                      <StageBadge age={p.age} />
                      {p.injury && (
                        <span className="pill pill-warning">
                          <HeartPulse size={10} /> {p.injury.name} · {Math.ceil(p.injury.daysRemaining)}d
                        </span>
                      )}
                    </div>
                    <div className="row-tile-sub">
                      {p.age} a&ntilde;os &middot; {p.matchesWon}V / {p.matchesPlayed - p.matchesWon}D
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRelease(p.id, p.name, p.salary)
                    }}
                    title="Rescindir contrato"
                    style={{ color: 'var(--danger)', background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    <UserMinus size={16} />
                  </button>
                </div>

                <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', alignItems: 'center' }}>
                  <span>
                    Nivel: <strong style={{ color: levelColor(p.level) }}>{p.level}</strong>
                    {p.level < p.potential && (
                      <span style={{ color: 'var(--text-secondary)' }}> / {p.potential}</span>
                    )}
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}>
                    Forma: {p.form} <FormTrend form={p.form} />
                  </span>
                  <span style={{ color: 'var(--text-secondary)' }}>
                    Rating: {Math.round(getEffectiveRating(p))}
                  </span>
                  <span className="money-negative" style={{ marginLeft: 'auto', fontWeight: 600 }}>
                    {p.salary.toLocaleString('es-ES')}€/sem
                  </span>
                </div>

                {isRising && p.level < p.potential && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div className="progress-track">
                      <div
                        className="progress-fill angled success"
                        style={{ width: `${(p.level / p.potential) * 100}%` }}
                      />
                    </div>
                    <span className="meta-text" style={{ whiteSpace: 'nowrap' }}>
                      {p.level} → {p.potential}
                    </span>
                    <MechanicHint
                      title="Crecimiento de promesas"
                      bullets={[
                        'Los jugadores menores de 23 años progresan cada mes hacia su potencial.',
                        'Un entrenador con buen skill acelera la progresión.',
                        'A partir de los 33 años el nivel empieza a declinar.',
                      ]}
                    />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </SectionDeck>
    </div>
  )
}
